import { Endpoints } from "@/shared/API/APIConfig";
import { baseApi, type ApiResponse } from "./base.api";
import type {
  AddItemVariationRequest,
  ApiGetPurchaseOrdersData,
  ApiPurchaseOrderDetails,
  CreatePOFormValues,
  GetItemsVariationParams,
  GetPurchaseOrdersDraft,
  GetPurchaseOrdersParams,
  GetPurchaseOrdersResult,
  ItemsVariationsResponse,
  POCategoriesItemsResponse,
  POHelpDataResponse,
  PurchaseOrderDetailsResponse,
} from "@/app/types/purchaseOrders.types";
import {
  mapApiPOToPO,
  mapApiPODetailsToDetails,
} from "./purchaseOrders.mapper";

export const purchaseOrdersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPurchaseOrders: builder.query<
      GetPurchaseOrdersResult,
      GetPurchaseOrdersParams
    >({
      query: ({
        page = 1,
        per_page = 10,
        search,
        supplier_id,
        status,
        order_type,
        date,
        creation_date,
        sort_by,
        sort_dir,
      }) => {
        const queryParams = new URLSearchParams();
        queryParams.set("page", String(page));
        queryParams.set("per_page", String(per_page));

        if (search) {
          queryParams.set("search", search);
        }

        if (supplier_id !== undefined) {
          if (Array.isArray(supplier_id)) {
            supplier_id.forEach((id, index) => {
              queryParams.append(`supplier_id[${index}]`, String(id));
            });
          } else {
            queryParams.set("supplier_id", String(supplier_id));
          }
        }

        if (status) {
          if (Array.isArray(status)) {
            status.forEach((st, index) => {
              queryParams.append(`status[${index}]`, st);
            });
          } else {
            queryParams.append("status[0]", status);
          }
        }

        if (order_type) {
          queryParams.set("order_type", order_type);
        }

        if (date) {
          queryParams.set("expected_delivery_date[0]", date);
        }

        if (sort_by) {
          queryParams.set("sort_by", sort_by);
        }

        if (sort_dir) {
          queryParams.set("sort_dir", sort_dir);
        }

        if (creation_date) {
          queryParams.set("created_at", creation_date);
        }
        return `${Endpoints.purchaseOrders.getAllPurchaseOrders}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<ApiGetPurchaseOrdersData>,
      ): GetPurchaseOrdersResult => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load purchase orders");
        }

        const poData = response.data;
        return {
          data: (poData.data ?? []).map(mapApiPOToPO),
          pagination: {
            page: poData.current_page ?? 1,
            limit: poData.per_page ?? 10,
            total: poData.total ?? 0,
            totalPages: poData.total_pages ?? 1,
          },
        };
      },
      keepUnusedDataFor: 0,
      providesTags: ["PurchaseOrders"],
    }),
    getPurchaseOrdersDraft: builder.query<
      GetPurchaseOrdersDraft,
      void
    >({
      query: () => {
        const queryParams = new URLSearchParams();
        queryParams.append("status[0]", "draft");

        return `${Endpoints.purchaseOrders.getAllPurchaseOrders}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<GetPurchaseOrdersDraft>,
      ): GetPurchaseOrdersDraft => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load purchase orders");
        }

        return response?.data;
      },
      providesTags: ["PurchaseOrders"],
    }),
    getPurchaseOrderById: builder.query<PurchaseOrderDetailsResponse, string>({
      query: (id) => Endpoints.purchaseOrders.getPurchaseOrderById(id),
      transformResponse: (
        response: ApiResponse<ApiPurchaseOrderDetails>,
      ): PurchaseOrderDetailsResponse => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load purchase order");
        }
        return mapApiPODetailsToDetails(response.data);
      },
      providesTags: (_result, _error, id) => [
        { type: "PurchaseOrders", id: String(id) },
      ],
      keepUnusedDataFor: 0,
    }),
    getPOHelpData: builder.query<POHelpDataResponse, void>({
      query: () => Endpoints.purchaseOrders.getHelpData,
      providesTags: ["HelpData"],
    }),

    cancelPurchaseOrder: builder.mutation<
      { success: boolean; message: string },
      string
    >({
      query: (id) => ({
        url: Endpoints.purchaseOrders.changeStatus(id),
        method: "POST",
        body: {
          status: "canceled",
        },
      }),
      invalidatesTags: ["PurchaseOrders"],
    }),

    rejectPurchaseOrder: builder.mutation<
      ApiResponse<unknown>,
      { id: string | number; reject_reason: string }
    >({
      query: ({ id, reject_reason }) => ({
        url: Endpoints.purchaseOrders.changeStatus(id),
        method: "POST",
        body: {
          status: "rejected",
          reject_reason,
        },
      }),
      invalidatesTags: ["PurchaseOrders"],
    }),
    getCategoriesItems: builder.query<POCategoriesItemsResponse[], string>({
      query: (id) => Endpoints.purchaseOrders.getCategories_items(id),
      transformResponse: (
        response: ApiResponse<POCategoriesItemsResponse[]>,
      ): POCategoriesItemsResponse[] => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load purchase order");
        }
        return response.data;
      },
      providesTags: (_result, _error, id) => [
        { type: "PurchaseOrders", id: String(id) },
      ],
    }),
    getItemVariations: builder.query<
      ItemsVariationsResponse[],
      GetItemsVariationParams
    >({
      query: ({ supplier_id, item_ids }) => {
        const queryParams = new URLSearchParams();
        queryParams.set("supplier_id", String(supplier_id));

        item_ids.forEach((item_id, index) => {
          queryParams.append(`item_ids[${index}]`, item_id);
        });

        return `${Endpoints.purchaseOrders.getItemVariations}?${queryParams.toString()}`;
      },
      transformResponse: (
        response: ApiResponse<ItemsVariationsResponse[]>,
      ): ItemsVariationsResponse[] => {
        if (!response.success) {
          throw new Error(response.message || "Failed to load purchase orders");
        }

        return response.data;
      },
      providesTags: ["PurchaseOrders"],
    }),
    createPurchaseOrder: builder.mutation<
      ApiResponse<CreatePOFormValues>,
      FormData
    >({
      query: (formData) => ({
        url: Endpoints.purchaseOrders.createPurchaseOrder,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["PurchaseOrders"],
    }),
    deletePurchaseOrders: builder.mutation<
      { success: boolean; message: string },
      number[]
    >({
      query: (ids) => {
        const queryParams = new URLSearchParams();
        ids.forEach((id, index) => {
          queryParams.append(`po_ids[${index}]`, String(id));
        });
        return {
          url: `${Endpoints.purchaseOrders.deletePurchaseOrders}?${queryParams.toString()}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["PurchaseOrders"],
    }),
    updatePODraft: builder.mutation<
      ApiResponse<CreatePOFormValues>,
      { id: string; data: FormData }
    >({
      query: ({ id, data }) => ({
        url: `${Endpoints.draft.updatePODraft}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PurchaseOrders"],
    }),
    addItemVariation: builder.mutation<
      ApiResponse<AddItemVariationRequest>,
      AddItemVariationRequest
    >({
      query: (formData) => ({
        url: Endpoints.purchaseOrders.addItemVariation,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["PurchaseOrders"],
    }),
    updateReceiver: builder.mutation<
      ApiResponse<unknown>,
      { id: string; data: FormData }
    >({
      query: ({ id, data }) => ({
        url: Endpoints.purchaseOrders.updateReceiver(id),
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PurchaseOrders"],
    }),
  }),
});

export const {
  useGetPurchaseOrdersQuery,
  useGetPurchaseOrderByIdQuery,
  useGetPOHelpDataQuery,
  useCancelPurchaseOrderMutation,
  useRejectPurchaseOrderMutation,
  useGetCategoriesItemsQuery,
  useGetItemVariationsQuery,
  useLazyGetItemVariationsQuery,
  useCreatePurchaseOrderMutation,
  useDeletePurchaseOrdersMutation,
  useUpdatePODraftMutation,
  useAddItemVariationMutation,
  useGetPurchaseOrdersDraftQuery,
  useUpdateReceiverMutation,
} = purchaseOrdersApi;
